import api from './api';

class EnhancedBackupService {
  // Create enhanced backup
  async createBackup(uploadToGoogleDrive = false) {
    try {
      const response = await api.post('/admin/backup/create', {
        uploadToGoogleDrive
      });
      return response.data;
    } catch (error) {
      console.error('Create backup error:', error);
      throw error;
    }
  }

  // Google Drive Authentication
  async getGoogleDriveStatus() {
    try {
      const response = await api.get('/admin/backup/google-drive/status');
      return response.data;
    } catch (error) {
      console.error('Google Drive status error:', error);
      throw error;
    }
  }

  async getGoogleDriveAuthUrl() {
    try {
      const response = await api.get('/admin/backup/google-drive/auth-url');
      return response.data;
    } catch (error) {
      console.error('Get Google Drive auth URL error:', error);
      throw error;
    }
  }

  async authenticateGoogleDrive(code) {
    try {
      const response = await api.post('/admin/backup/google-drive/auth', { code });
      return response.data;
    } catch (error) {
      console.error('Google Drive auth error:', error);
      throw error;
    }
  }

  async disconnectGoogleDrive() {
    try {
      const response = await api.delete('/admin/backup/google-drive/disconnect');
      return response.data;
    } catch (error) {
      console.error('Google Drive disconnect error:', error);
      throw error;
    }
  }

  // List backups
  async listGoogleDriveBackups() {
    try {
      const response = await api.get('/admin/backup/google-drive/list');
      return response.data;
    } catch (error) {
      console.error('List Google Drive backups error:', error);
      throw error;
    }
  }

  async listLocalBackups() {
    try {
      const response = await api.get('/admin/backup/local/list');
      return response.data;
    } catch (error) {
      console.error('List local backups error:', error);
      throw error;
    }
  }

  // Backup Scheduler
  async getSchedulerStatus() {
    try {
      const response = await api.get('/admin/backup/scheduler/status');
      return response.data;
    } catch (error) {
      console.error('Scheduler status error:', error);
      throw error;
    }
  }

  async updateSchedulerSettings(settings) {
    try {
      const response = await api.post('/admin/backup/scheduler/settings', settings);
      return response.data;
    } catch (error) {
      console.error('Update scheduler settings error:', error);
      throw error;
    }
  }

  async startScheduler() {
    try {
      const response = await api.post('/admin/backup/scheduler/start');
      return response.data;
    } catch (error) {
      console.error('Start scheduler error:', error);
      throw error;
    }
  }

  async stopScheduler() {
    try {
      const response = await api.post('/admin/backup/scheduler/stop');
      return response.data;
    } catch (error) {
      console.error('Stop scheduler error:', error);
      throw error;
    }
  }

  // Utility methods
  openGoogleDriveAuthWindow(authUrl) {
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      authUrl,
      'google-drive-auth',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );

    return new Promise((resolve, reject) => {
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          reject(new Error('Authentication window was closed'));
        }
      }, 1000);

      // Listen for messages from the popup
      const messageListener = (event) => {
        // Accept messages from both frontend and backend origins
        const allowedOrigins = [
          window.location.origin,
          'http://localhost:5000',
          'http://localhost:3000'
        ];

        if (!allowedOrigins.includes(event.origin)) return;

        if (event.data.type === 'GOOGLE_DRIVE_AUTH_SUCCESS') {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
          popup.close();
          resolve(event.data.code);
        } else if (event.data.type === 'GOOGLE_DRIVE_AUTH_ERROR') {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
          popup.close();
          reject(new Error(event.data.error));
        }
      };

      window.addEventListener('message', messageListener);
    });
  }

  formatBackupSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getBackupTypeIcon(type) {
    switch (type) {
      case 'manual':
        return '🔧';
      case 'scheduled':
        return '⏰';
      case 'automatic':
        return '🤖';
      default:
        return '📦';
    }
  }

  getBackupStatusColor(status) {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      case 'in_progress':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  }

  downloadBackupAsJson(backupData, filename) {
    const dataStr = JSON.stringify(backupData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = filename || `sunx-backup-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }

  validateBackupFile(file) {
    if (!file) {
      throw new Error('No file selected');
    }

    // Check for ZIP files (our backup format)
    if (file.type !== 'application/zip' &&
        file.type !== 'application/x-zip-compressed' &&
        !file.name.endsWith('.zip')) {
      throw new Error('Please select a valid ZIP backup file');
    }

    // Check if it's a backup file based on naming pattern
    if (!file.name.startsWith('sunx-backup-') || !file.name.endsWith('.zip')) {
      throw new Error('Please select a valid SunX backup file (must start with "sunx-backup-" and end with ".zip")');
    }

    if (file.size > 100 * 1024 * 1024) { // 100MB limit
      throw new Error('Backup file is too large (max 100MB)');
    }

    return true;
  }

  async parseBackupFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const backupData = JSON.parse(e.target.result);

          // Validate backup structure
          if (!backupData.data || !backupData.metadata) {
            throw new Error('Invalid backup file structure');
          }

          resolve(backupData);
        } catch (error) {
          reject(new Error('Invalid JSON format or corrupted backup file'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read backup file'));
      };

      reader.readAsText(file);
    });
  }

  // Restore from uploaded backup file
  async restoreFromFile(file) {
    try {
      // Validate file first
      this.validateBackupFile(file);

      const formData = new FormData();
      formData.append('backupFile', file);

      const response = await api.post('/admin/backup/restore-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Restore from file error:', error);
      throw error;
    }
  }

  // Restore from backup data (JSON)
  async restoreFromData(backupData) {
    try {
      const response = await api.post('/admin/backup/restore', {
        backupData
      });
      return response.data;
    } catch (error) {
      console.error('Restore from data error:', error);
      throw error;
    }
  }

  // Get restore preview (analyze backup file without restoring)
  async getRestorePreview(file) {
    try {
      this.validateBackupFile(file);

      // For ZIP files, we can't easily preview without server-side extraction
      // For now, return basic file info
      return {
        fileName: file.name,
        fileSize: file.size,
        lastModified: new Date(file.lastModified),
        type: file.type
      };
    } catch (error) {
      console.error('Get restore preview error:', error);
      throw error;
    }
  }

  // Download backup file
  async downloadBackup(fileName) {
    try {
      const response = await api.get(`/admin/backup/download/${fileName}`, {
        responseType: 'blob'
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, message: 'Backup downloaded successfully' };
    } catch (error) {
      console.error('Download backup error:', error);
      throw error;
    }
  }

  // Delete backup file
  async deleteBackup(fileName) {
    try {
      const response = await api.delete(`/admin/backup/delete/${fileName}`);
      return response.data;
    } catch (error) {
      console.error('Delete backup error:', error);
      throw error;
    }
  }
}

export const enhancedBackupService = new EnhancedBackupService();
